import { A } from '@ember/array';
import type NativeArray from '@ember/array/-private/native-array';
import type ArrayProxy from '@ember/array/proxy';
import { get, setProperties } from '@ember/object';
import Route from '@ember/routing/route';
import type RouterService from '@ember/routing/router-service';
import type Transition from '@ember/routing/transition';
import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import { ForbiddenError } from '@ember-data/adapter/error';
import type { SyncHasMany } from '@ember-data/model';
import type StoreService from '@ember-data/store';
import type AbilitiesService from 'ember-can/services/abilities';
import type AgreeableNdaModel from 'garaje/models/agreeable-nda';
import type ConfigModel from 'garaje/models/config';
import type EntryModel from 'garaje/models/entry';
import type EventReportModel from 'garaje/models/event-report';
import type LocationModel from 'garaje/models/location';
import type PlatformJobModel from 'garaje/models/platform-job';
import type PrinterModel from 'garaje/models/printer';
import type SignInFieldModel from 'garaje/models/sign-in-field';
import type TenantModel from 'garaje/models/tenant';
import type UserModel from 'garaje/models/user';
import type UserDocumentTemplateConfiguration from 'garaje/models/user-document-template-configuration';
import type CurrentAdminService from 'garaje/services/current-admin';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlowService from 'garaje/services/flow';
import type LoggerService from 'garaje/services/logger';
import type SkinnyLocationsService from 'garaje/services/skinny-locations';
import type StateService from 'garaje/services/state';
import { routeEvent } from 'garaje/utils/decorators/route';
import swallowForbidden from 'garaje/utils/swallow-forbidden';
import throwUnlessTaskDidCancel from 'garaje/utils/throw-unless-task-did-cancel';
import type { RecordArray } from 'garaje/utils/type-utils';
import moment from 'moment-timezone';
import { hash } from 'rsvp';
import type ServerError from 'utils/server-error';

import type VisitorsEntryController from './controller';

interface VisitorsEntryParams {
  entry_id: string;
}

type CurrentFlowSignInFieldPage = Awaited<NonNullable<EntryModel['currentFlow']>['signInFieldPage']>;

export interface VisitorsEntryModel {
  agreeableNdas: SyncHasMany<AgreeableNdaModel>;
  entry: EntryModel;
  config: ConfigModel;
  connectedTenants: RecordArray<TenantModel> | TenantModel[];
  eventReports: Array<EventReportModel | PlatformJobModel>;
  blocklistContacts: ArrayProxy<UserModel> | UserModel[];
  idScanContacts: ArrayProxy<UserModel> | UserModel[];
  printers: PrinterModel[] | RecordArray<PrinterModel>;
  vrSubscription: StateService['vrSubscription'];
  signInFields?: ArrayProxy<SignInFieldModel>;
}

class VisitorsEntryRoute extends Route {
  declare controller: VisitorsEntryController;

  @service declare abilities: AbilitiesService;
  @service declare currentAdmin: CurrentAdminService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare flow: FlowService;
  @service declare logger: LoggerService;
  @service declare router: RouterService;
  @service declare skinnyLocations: SkinnyLocationsService;
  @service declare state: StateService;
  @service declare store: StoreService;

  beforeModel(): void {
    const { vrSubscription } = this.state;
    if (!(isPresent(vrSubscription) && this.abilities.can('visit dashboard'))) {
      if (this.abilities.can('visit dashboard')) {
        this.router.transitionTo('visitors.start-trial');
      } else if (this.abilities.can('visit billing')) {
        this.router.transitionTo('billing');
      } else if (this.abilities.can('visit security-desk')) {
        this.router.transitionTo('security-desk');
      } else {
        this.router.transitionTo('unauthorized');
      }
    }

    this.skinnyLocations.loadAllTask.perform().catch(throwUnlessTaskDidCancel);
  }

  async model(params: VisitorsEntryParams): Promise<VisitorsEntryModel> {
    let printers: Promise<RecordArray<PrinterModel> | PrinterModel[]> | PrinterModel[] = [];
    const { vrSubscription, currentLocation } = this.state;
    const { blocklistContacts, idScanContacts, config } = currentLocation;

    // Printers endpoint returns 403 for unauthorized employees which end up on users
    // getting sad cloud upon sign in
    if (this.abilities.can('visit printers') && vrSubscription?.canEnableBadgePrinting) {
      printers = swallowForbidden(
        this.store.query('printer', {
          filter: { location: currentLocation.id, enabled: true },
          include: 'devices,devices.failover-printer',
          sort: 'status',
        }),
        [] as PrinterModel[],
      );
    }

    const connectedTenants = await swallowForbidden(currentLocation.getPropertyConnections(), [] as TenantModel[]);
    const include = [
      'flow',
      'flow.flow-questionnaires',
      'flow.flow-questionnaires.questionnaire',
      'flow-version',
      'questionnaire-response-summary',
      'visit-approval',
      'visit-approval.screener-results',
      'visit-approval.screener-reviews',
      'visitor-profile',
    ];

    const fields: Record<string, string> = {
      'visitor-profiles': 'id',
    };

    if (connectedTenants.length) include.push('invite-multi-tenancy-visitor-notification-logs');

    if (this.state.features?.canAccessMultipleHosts) {
      include.push('employee', 'additional-hosts');
    }

    const entry = await currentLocation.findEntry(params.entry_id, include, fields).catch((err) => {
      if ((<ServerError>err).errors && /404/.test((<ServerError>err).errors.toString())) {
        this.router.transitionTo('visitors.entries');
      } else {
        throw err;
      }
    });

    // employee result is discarded but the await primes ember-data's identity
    // map so the entry-fields template reads `entry.employee` synchronously on
    // the happy path. On a 403, the relationship resolves to a proxy wrapping
    // null — the template's `{{if @entry.employee ...}}` check sees a truthy
    // proxy regardless, so HostsField receives an empty proxy rather than the
    // name-only fallback (pre-existing concern; outside this PR's scope).
    // allSettled keeps both fetches parallel while letting either fail with a 403 independently.
    let entryLocation: LocationModel | null = null;
    const [locResult, empResult] = await Promise.allSettled([entry!.location, entry!.employee]);
    if (locResult.status === 'fulfilled') {
      entryLocation = locResult.value ?? null;
    } else if (!(locResult.reason instanceof ForbiddenError)) {
      throw locResult.reason;
    }
    if (empResult.status === 'rejected' && !(empResult.reason instanceof ForbiddenError)) {
      throw empResult.reason;
    }

    if (entryLocation) {
      this.maybeSwitchCurrentLocation(currentLocation, entryLocation);
    } else {
      const entryLocationId = entry!.belongsTo('location').id();
      if (entryLocationId && entryLocationId !== currentLocation.id) {
        // Cross-location entry without read access — UI silently renders under
        // currentLocation's chrome. Wrap in Error so LoggerService's
        // `console.warn(error)` shows the wrapped error; passing a plain string
        // would log `undefined` because the service's console.warn ignores its
        // `message` argument and only logs `error`. Include the user's roles
        // so a Datadog alert can identify the misconfigured custom role
        // without a separate lookup.
        // Empty userRoles is almost certainly a setRolesTask load race rather
        // than a real "no roles" user — the warn only fires when the user
        // could read the entry, which requires at least one role.
        const userRoles = this.currentAdmin.userRoles.length
          ? this.currentAdmin.userRoles.map((r) => `${r.roleName}(${r.id})`).join(', ')
          : 'unloaded';
        this.logger.warn(
          new Error(
            `SQ-7291: cross-location entry ${entry!.id} (location ${entryLocationId}) viewed without location read; user ${this.currentAdmin.id} roles=[${userRoles}]`,
          ),
        );
      }
    }

    let versionedSignInFields: ArrayProxy<SignInFieldModel> | undefined;

    if (entry!.currentFlow) {
      // get() returns a PromiseProxyMixin (or sync value), not a native
      // Promise<T>, so we use try/catch rather than swallowForbidden — the
      // helper's signature requires Promise<T>, and wrapping with
      // Promise.resolve() to satisfy it would add a normalization step
      // for no behavioral gain.
      let activeUserDocumentTemplateConfigurations: UserDocumentTemplateConfiguration[] = [];
      try {
        // eslint-disable-next-line ember/no-get
        activeUserDocumentTemplateConfigurations = ((await get(
          entry,
          'currentFlow.activeUserDocumentTemplateConfigurations',
        )) || []) as UserDocumentTemplateConfiguration[];
      } catch (e) {
        if (!(e instanceof ForbiddenError)) throw e;
      }
      const activeUserDocumentTemplateConfigurationCount = activeUserDocumentTemplateConfigurations.length;

      let signInFieldPage: CurrentFlowSignInFieldPage | undefined;
      try {
        // eslint-disable-next-line ember/no-get
        signInFieldPage = await get(entry!.currentFlow, 'signInFieldPage');
      } catch (e) {
        if (!(e instanceof ForbiddenError)) throw e;
      }

      if (this.shouldUseCustomSignInFields(entry!)) {
        const includes = [
          'sign-in-field-actions',
          'actionable-sign-in-field-actions',
          'actionable-sign-in-fields',
          'flow-version',
        ].join(',');

        // Widening cast: store.query returns RecordArray<T>; VisitorsEntryModel
        // declares ArrayProxy<T> (RecordArray extends ArrayProxy). Not a type lie.
        versionedSignInFields = (await swallowForbidden(
          this.store.query('entry-sign-in-field', { entryId: entry!.id, include: includes }),
          undefined,
        )) as ArrayProxy<SignInFieldModel> | undefined;
      } else if (signInFieldPage) {
        // Prime the identity map: signInFields is a hasMany that downstream
        // sign-in-field templates read synchronously. Same try/catch shape as
        // the get-based reads above.
        try {
          await signInFieldPage.signInFields;
        } catch (e) {
          if (!(e instanceof ForbiddenError)) throw e;
        }
      }

      const isDocUploadsEnabled = this.abilities.can('access document-upload');

      // eslint-disable-next-line ember/no-get
      if (get(entry!.currentFlow, 'isProtect')) {
        // eslint-disable-next-line ember/no-get
        await swallowForbidden(this.flow.getFlowWithIncludes(get(entry!.currentFlow, 'id')), undefined);
      }

      if (isDocUploadsEnabled || activeUserDocumentTemplateConfigurationCount) {
        // Fetch associated visitor documents. user-document-links include only
        // matters when the feature is on (powers approval status); custom
        // uploads aren't in active configs.
        const include = isDocUploadsEnabled
          ? 'user-document-template,user-document-links,user-document-attachments,user-document-attachments.user-document-template-attachment'
          : 'user-document-template,user-document-attachments,user-document-attachments.user-document-template-attachment';
        try {
          entry!.visitorDocuments = await this.store.query('visitor-document', {
            include,
            filter: { 'entry-id': entry!.id },
          });
        } catch (e) {
          // 403 → silent swallow (matches the PR's defensive pattern). Other
          // errors are also swallowed (pre-existing broad-swallow — visitor
          // documents are optional and shouldn't block entry detail on flaky
          // 5xx) but logged so we have a Datadog signal.
          if (!(e instanceof ForbiddenError)) {
            this.logger.warn(e instanceof Error ? e : new Error(String(e)));
          }
        }
      }

      // Load document-upload-page with its custom templates for the document review tab.
      // Uses a separate /flows/:id fetch (vs. nested include on the entry) because
      // JSONAPI:Resources' STI model_hints route Flows::Global and Flows::Property to
      // dedicated resources that don't declare has_one :document_upload_page — so a
      // nested flow.document-upload-page include silently fails for those STI
      // subclasses. The /flows/:id endpoint serializes through FlowResource
      // regardless of subclass, which has the relation.
      if (isDocUploadsEnabled) {
        // eslint-disable-next-line ember/no-get
        const flowId = get(entry!.currentFlow, 'id');
        const reloadedFlow = await swallowForbidden(
          this.store.findRecord('flow', flowId, {
            include:
              'document-upload-page,document-upload-page.custom-user-document-templates,document-upload-page.user-document-template-configurations,document-upload-page.user-document-template-configurations.user-document-template',
            reload: true,
          }),
          null,
        );
        // Load per-page template configurations (reUploadConfig policies)
        const page = reloadedFlow?.belongsTo('documentUploadPage').value();
        if (page) {
          const adapter = this.store.adapterFor('document-upload-page') as unknown as {
            fetchConfigurations(id: string): Promise<Record<string, unknown>>;
          };
          const payload = await swallowForbidden(adapter.fetchConfigurations(page.id), null);
          if (payload) {
            this.store.pushPayload(payload);
          }
        }
      }
    }

    return hash({
      agreeableNdas: entry!.agreeableNdas,
      entry: entry!,
      config,
      connectedTenants,
      eventReports: [],
      blocklistContacts: swallowForbidden(blocklistContacts, [] as UserModel[]),
      idScanContacts: swallowForbidden(idScanContacts, [] as UserModel[]),
      printers,
      vrSubscription,
      signInFields: versionedSignInFields,
    });
  }

  setupController(controller: VisitorsEntryController, model: VisitorsEntryModel, transition: Transition): void {
    super.setupController(controller, model, transition);

    setProperties(controller, {
      model: model.entry,
      connectedTenants: model.connectedTenants,
      config: model.config,
      agreeableNdas: model.agreeableNdas,
      agreements: model.agreeableNdas.map((agreeableNda) => agreeableNda.agreement),
      printers: model.printers,
      vrSubscription: model.vrSubscription,
      eventReports: model.eventReports,
      blocklistContacts: model.blocklistContacts,
      idScanContacts: model.idScanContacts,
    });

    // Assign separately: setProperties can't infer ArrayProxy.content as NativeArray
    controller._routeSignInFields = (model.signInFields?.content as NativeArray<SignInFieldModel>) ?? null;

    controller._buildUserDataChangeset();
    controller._buildStaticFields();
    void controller.loadPreviousEntries.perform();
    void controller.pollEventReports.cancelAll();
    void controller.pollEventReports.perform();
  }

  resetController(controller: VisitorsEntryController): void {
    controller.previousEntries = A([]);
    controller.activeTab = 'visit-details';
    // Clear the sticky returnTo* QPs on full route exit so they can't leak
    // onto an unrelated entry visited later in the same session.
    controller.returnTo = null;
    controller.returnToModel = null;
    controller.returnToTab = null;
    void controller.pollEventReports.cancelAll();
  }

  deactivate(transition: Transition): void {
    super.deactivate(transition);
    // eslint-disable-next-line ember/no-controller-access-in-routes
    void this.controller.pollEventReports.cancelAll();
  }

  titleToken(): string {
    // eslint-disable-next-line ember/no-controller-access-in-routes
    return `${this.controller.model.fullName} · ${moment(this.controller.model.signInTime).format('MM-DD-YYYY')}`;
  }

  maybeSwitchCurrentLocation(currentLocation: LocationModel, entryLocation: LocationModel): void {
    if (currentLocation.id !== entryLocation.id) {
      // eslint-disable-next-line ember/no-controller-access-in-routes
      const protectedController = this.controllerFor('protected');
      protectedController.send('switchLocation', entryLocation.id);
    }
  }

  @routeEvent
  routeWillChange(transition: Transition): boolean {
    // eslint-disable-next-line ember/no-controller-access-in-routes
    const hasUnsavedChanges = this.controller.hasChanges;

    if (hasUnsavedChanges && !window.confirm('You will lose any unsaved changes. Are you sure you want to continue?')) {
      transition.abort();

      return false;
    }

    // eslint-disable-next-line ember/no-controller-access-in-routes
    this.controller.rollbackChanges();
    return true;
  }

  /**
   * Determines whether to fetch sign-in fields from the entry-specific endpoint
   * @param entry - The entry model
   * @returns true if should use custom endpoint, false otherwise
   */
  shouldUseCustomSignInFields(entry: EntryModel): boolean {
    if (!this.featureFlags.isEnabled('ava-entry-versioned-fields')) return false;
    const flowVersionId = entry.belongsTo('flowVersion').id();
    return !!flowVersionId;
  }
}

export default VisitorsEntryRoute;
